import React, { useState } from 'react';
import { Share2, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from '../utils/translations';

const APP_URL = "https://apps.apple.com/ro/app/askuniverse-app/id6775565419?l=ro";

const ShareApp = ({ variant = 'default', showCopyButton = true, className = '' }) => {
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const [copied, setCopied] = useState(false);

  const getShareData = () => ({
    title: t('shareAppTitle'),
    text: t('shareAppText'),
    url: APP_URL
  });

  const handleShare = async () => {
    const shareData = getShareData();
    
    // Try native share (mobile)
    if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
      try {
        await navigator.share(shareData);
        return;
      } catch (error) {
        if (error.name !== 'AbortError') {
          // User cancelled, try clipboard fallback
          handleCopyLink();
        }
      }
    } else {
      // Desktop fallback - copy to clipboard
      handleCopyLink();
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(APP_URL);
      setCopied(true);
      toast.success(t('linkCopied'));
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error(t('copyFailed'));
    }
  };

  // Different style variants
  const variants = {
    default: {
      share: 'px-4 py-2 bg-violet-600 hover:bg-violet-500 text-white rounded-lg font-medium transition-all flex items-center gap-2',
      copy: 'px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg font-medium transition-all flex items-center gap-2'
    },
    pill: {
      share: 'px-6 py-3 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white rounded-full font-medium transition-all flex items-center gap-2',
      copy: 'px-4 py-3 bg-slate-800/50 hover:bg-slate-800 text-slate-300 rounded-full font-medium transition-all flex items-center gap-2 border border-white/10'
    },
    compact: {
      share: 'p-2 bg-violet-600/20 hover:bg-violet-600/30 text-violet-400 rounded-lg transition-all',
      copy: 'p-2 bg-slate-800/50 hover:bg-slate-800 text-slate-400 rounded-lg transition-all'
    },
    ghost: {
      share: 'px-3 py-2 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg transition-all flex items-center gap-2 text-sm',
      copy: 'px-3 py-2 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg transition-all flex items-center gap-2 text-sm'
    }
  };

  const styles = variants[variant] || variants.default;

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <button
        onClick={handleShare}
        className={styles.share}
        data-testid="share-app-btn"
        title={t('shareApp')}
      >
        <Share2 size={variant === 'compact' ? 18 : 16} />
        {variant !== 'compact' && <span>{t('shareApp')}</span>}
      </button>
      
      {showCopyButton && (
        <button
          onClick={handleCopyLink}
          className={styles.copy}
          data-testid="copy-link-btn"
          title={t('copyLink')}
        >
          {copied ? (
            <Check size={variant === 'compact' ? 18 : 16} className="text-emerald-400" />
          ) : (
            <Copy size={variant === 'compact' ? 18 : 16} />
          )}
          {variant !== 'compact' && <span>{t('copyLink')}</span>}
        </button>
      )}
    </div>
  );
};

export default ShareApp;
